import api from './api';

// ============================================
// Ranglisten für Vereine/Ligen/Verbände/Spielstätten (Marcel 2026-09-29).
// Backend: /api/rankings/:listingType/:listingId… – ALLES nullable parsen (BC).
// ============================================

export type RankingListingType = 'club' | 'league' | 'association' | 'pub';

export interface RankingColumn { key: string; label: string; type?: 'number' | 'text' | null }
export interface RankingLinkedUser { userId: string; displayName?: string | null; username?: string | null; image?: string | null }
export interface RankingEntry {
  id?: string | null;
  rank: number;
  playerName: string;
  points?: number | null;
  note?: string | null;
  values?: Record<string, number | string | null> | null;
  image?: string | null;
  userId?: string | null;
  linkedUser?: RankingLinkedUser | null;
}
export interface RankingEventResult { rank?: number | null; playerName: string; points?: number | null; participants?: number | null }
export interface RankingEvent { id?: string | null; name: string; date?: string | null; order?: number | null; results?: RankingEventResult[] | null }
export interface Ranking {
  /** eigener Slug + kanonischer Pfad der Ranglisten-Seite (2026-09-29) */
  slug?: string | null;
  path?: string | null;
  id: string;
  listingType?: string | null;
  listingId?: string | null;
  title: string;
  titleEn?: string | null;
  description?: string | null;
  descriptionEn?: string | null;
  legend?: string | null;
  season?: string | null;
  unitLabel?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  columns?: RankingColumn[] | null;
  entries?: RankingEntry[] | null;
  entryCount?: number | null;
  events?: RankingEvent[] | null;
  eventCount?: number | null;
  visible?: boolean | null;
  sortOrder?: number | null;
  source?: string | null;
  lastImportAt?: number | null;
  createdAt?: number | null;
  updatedAt?: number | null;
}
/** PURE (Marcel 2026-10-06): „Zuletzt aktualisiert“ = jüngster Zeitpunkt aus updatedAt/lastImportAt, sonst null */
export function rankingLastUpdated(r: Pick<Ranking, 'updatedAt' | 'lastImportAt'> | null | undefined): number | null {
  const m = Math.max(Number(r?.updatedAt ?? 0), Number(r?.lastImportAt ?? 0));
  return Number.isFinite(m) && m > 0 ? m : null;
}
export function formatRankingDate(ms: number): string {
  return new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
/** PURE: 409-Antwort des Schrumpf-Schutzes (Backend: SHRINK_CONFIRM) */
export function isShrinkConflict(error: any): boolean {
  return error?.response?.status === 409;
}
/** Body für Anlegen/Ändern – Anzeigefelder werden vom Backend verworfen. */
export interface RankingBody {
  /** Schrumpf-Schutz (2026-10-06): true = Entfernen vieler Plätze/Teilwertungen ist beabsichtigt */
  confirmShrink?: boolean;
  title: string;
  titleEn?: string | null;
  description?: string | null;
  descriptionEn?: string | null;
  legend?: string | null;
  season?: string | null;
  unitLabel?: string | null;
  sortDirection?: 'asc' | 'desc';
  columns?: RankingColumn[];
  entries?: Array<Pick<RankingEntry, 'id' | 'rank' | 'playerName' | 'points' | 'note' | 'values' | 'image'>>;
  events?: RankingEvent[];
  visible?: boolean;
  sortOrder?: number | null;
}
export interface RankingLinkRequest {
  id: string;
  listingType?: string | null;
  listingId?: string | null;
  rankingId?: string | null;
  entryId?: string | null;
  requesterId?: string | null;
  message?: string | null;
  rankingTitle?: string | null;
  entryRank?: number | null;
  entryPlayerName?: string | null;
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled' | null;
  decisionMessage?: string | null;
  createdAt?: number | null;
  requester?: RankingLinkedUser | null;
}
export interface RankingFollower { userId: string; username?: string | null; image?: string | null }
/** importQuota: Tageslimit KI-Import (2026-09-29) – null ohne Freischaltung oder bei altem Backend */
export interface RankingFeatureFlags { rankingAi?: boolean | null; importQuota?: { limit: number; used: number; remaining: number; exhausted: boolean; resetsAt?: string | null } | null }
export interface ImportSheet { name: string; rowCount?: number | null; colCount?: number | null; namedRankRows?: number | null; isEmptyTemplate?: boolean | null }
export interface ImportPreviewRanking extends Omit<Ranking, 'id'> {
  sourceSheet?: string | null;
  matchedRankingId?: string | null;
  confidence?: number | null;
  warnings?: string[] | null;
  removedEntries?: number | null;
}
export interface ImportPreview {
  rankings?: ImportPreviewRanking[] | null;
  skipped?: Array<{ sheet?: string | null; reason?: string | null }> | null;
  warnings?: string[] | null;
  summary?: { found?: number | null; kept?: number | null; removedEntries?: number | null } | null;
  existing?: Array<{ id: string; title: string; entryCount?: number | null }> | null;
}
export interface ImportJob {
  id: string;
  status?: 'queued' | 'running' | 'preview' | 'applied' | 'failed' | 'cancelled' | null;
  fileName?: string | null;
  fileKind?: string | null;
  sheets?: ImportSheet[] | null;
  preview?: ImportPreview | null;
  applied?: { created?: Array<{ index: number; rankingId: string; title: string; entryCount?: number }>; updated?: Array<{ index: number; rankingId: string; title: string; entryCount?: number }>; skipped?: Array<{ index: number; reason: string }> } | null;
  error?: string | null;
  createdAt?: number | null;
  finishedAt?: number | null;
}
/** Antwort der eigenen Ranglisten-Seite GET /api/ranking-page/:type/:listingSlug/:rankingSlug */
export interface RankingPageListing { type: RankingListingType; id: string; slug: string; name?: string | null; image?: string | null; path: string }
export interface RankingPageSibling { id: string; slug?: string | null; path?: string | null; title: string; season?: string | null; entryCount?: number | null }
/** „Das bin ich“-Dialog je Listing (Marcel 2026-10-06): eigener Hinweistext + Nachrichten-Pflicht */
export interface RankingClaimSettings { claimHint?: string | null; claimMessageRequired?: boolean | null }
export const DEFAULT_CLAIM_SETTINGS: RankingClaimSettings = { claimHint: null, claimMessageRequired: false };
export interface RankingPage { listing: RankingPageListing; ranking: Ranking; rankings: RankingPageSibling[]; claimSettings?: RankingClaimSettings | null }
export const RANKING_ROUTE_PREFIX: Record<RankingListingType, string> = { club: 'verein', league: 'liga', association: 'verband', pub: 'spielstaette' };
export const RANKING_PREFIX_TO_TYPE: Record<string, RankingListingType> = { verein: 'club', liga: 'league', verband: 'association', spielstaette: 'pub' };
export const rankingPagePath = (type: RankingListingType, listingSlug?: string | null, rankingSlug?: string | null): string | null =>
  (listingSlug && rankingSlug ? `/${RANKING_ROUTE_PREFIX[type]}/${listingSlug}/rangliste/${rankingSlug}` : null);
/** Profil-Tab „Ranglisten“: Ranglisten, in denen ein Nutzer verknüpft ist (GET /api/rankings/user/:userId) */
/** Startseite „Zuletzt aktualisierte Ranglisten“ (2026-10-20, GET /api/rankings/recent) */
export interface RecentRankingTopEntry { rank: number | null; playerName: string; points: number | null }
export interface RecentRankingItem {
  id: string; slug: string; path: string; title: string; season: string | null; entryCount: number; lastUpdated: number;
  listing: RankingPageListing; top: RecentRankingTopEntry[];
}

export interface UserRankingItem {
  ranking: { id: string; slug?: string | null; path?: string | null; title: string; season?: string | null; unitLabel?: string | null; entryCount?: number | null; updatedAt?: number | null };
  listing: { type: RankingListingType; id: string; slug: string; name?: string | null; image?: string | null; path: string };
  entry: { id?: string | null; rank: number; playerName: string; points?: number | string | null; note?: string | null };
}
export interface ImportSelection { index: number; action: 'create' | 'update' | 'skip'; targetRankingId?: string | null }

const base = (type: RankingListingType, listingId: string) => `/api/rankings/${type}/${encodeURIComponent(listingId)}`;

/** Fehlertext aus einer axios-Antwort (Backend sendet Text-Bodies oder { message }). */
export function extractRankingError(error: any, fallback: string): string {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data.message === 'string') return data.message;
  return fallback;
}

/** Datei → Base64 (Data-URI) für den KI-Import. */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
    reader.readAsDataURL(file);
  });
}

/** Sortierung für die Anzeige: Rang, dann Punkte je Richtung, dann Name (Spiegel der Backend-Regel). */
export function sortRankingEntries(entries: RankingEntry[], sortDirection: 'asc' | 'desc' | null | undefined = 'desc'): RankingEntry[] {
  const dir = sortDirection === 'asc' ? 1 : -1;
  return [...entries].sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    const pa = a.points ?? null; const pb = b.points ?? null;
    if (pa !== pb) { if (pa == null) return 1; if (pb == null) return -1; return (pa - pb) * dir; }
    return String(a.playerName).localeCompare(String(b.playerName), 'de');
  });
}

class RankingService {
  /** top = nur die ersten N Plätze je Rangliste (Tab-Vorschau), entryCount bleibt die Gesamtzahl */
  async list(type: RankingListingType, listingId: string, { all = false, top = null }: { all?: boolean; top?: number | null } = {}): Promise<Ranking[]> {
    const params = [all ? 'all=1' : null, top ? `top=${top}` : null].filter(Boolean).join('&');
    const res = await api.get(`${base(type, listingId)}${params ? `?${params}` : ''}`);
    return Array.isArray(res.data) ? res.data : [];
  }
  async recent(limit = 12): Promise<RecentRankingItem[]> {
    const res = await api.get(`/api/rankings/recent?limit=${encodeURIComponent(String(limit))}`);
    const list = Array.isArray(res?.data) ? res.data : [];
    return list.filter((i: any) => i && typeof i === 'object' && i.id && i.path && i.title);
  }
  async forUser(userId: string): Promise<UserRankingItem[]> {
    const res = await api.get(`/api/rankings/user/${encodeURIComponent(userId)}`);
    return Array.isArray(res.data) ? res.data : [];
  }
  async getPage(type: RankingListingType, listingSlug: string, rankingSlug: string): Promise<RankingPage> {
    const res = await api.get(`/api/ranking-page/${type}/${encodeURIComponent(listingSlug)}/${encodeURIComponent(rankingSlug)}`);
    return res.data as RankingPage;
  }
  async get(type: RankingListingType, listingId: string, rankingId: string): Promise<Ranking | null> {
    const res = await api.get(`${base(type, listingId)}/${encodeURIComponent(rankingId)}`);
    return res.data ?? null;
  }
  async create(type: RankingListingType, listingId: string, body: RankingBody): Promise<Ranking> {
    const res = await api.post(base(type, listingId), body);
    return res.data;
  }
  async update(type: RankingListingType, listingId: string, rankingId: string, body: RankingBody): Promise<Ranking> {
    const res = await api.put(`${base(type, listingId)}/${encodeURIComponent(rankingId)}`, body);
    return res.data;
  }
  async remove(type: RankingListingType, listingId: string, rankingId: string): Promise<void> {
    await api.post(`${base(type, listingId)}/${encodeURIComponent(rankingId)}/delete`, {});
  }
  async reorder(type: RankingListingType, listingId: string, order: string[]): Promise<Ranking[]> {
    const res = await api.post(`${base(type, listingId)}/reorder`, { order });
    return Array.isArray(res.data) ? res.data : [];
  }
  async link(type: RankingListingType, listingId: string, rankingId: string, entryId: string, userId: string): Promise<Ranking> {
    const res = await api.post(`${base(type, listingId)}/${encodeURIComponent(rankingId)}/entries/${encodeURIComponent(entryId)}/link`, { userId });
    return res.data;
  }
  async unlink(type: RankingListingType, listingId: string, rankingId: string, entryId: string): Promise<Ranking> {
    const res = await api.post(`${base(type, listingId)}/${encodeURIComponent(rankingId)}/entries/${encodeURIComponent(entryId)}/unlink`, {});
    return res.data;
  }
  async followers(type: RankingListingType, listingId: string, { limit = 100, offset = 0 } = {}): Promise<{ followers: RankingFollower[]; total: number }> {
    const res = await api.get(`${base(type, listingId)}/followers?limit=${limit}&offset=${offset}`);
    return { followers: Array.isArray(res.data?.followers) ? res.data.followers : [], total: res.data?.total ?? 0 };
  }
  async requestLink(type: RankingListingType, listingId: string, rankingId: string, entryId: string, message?: string | null): Promise<RankingLinkRequest> {
    const res = await api.post(`${base(type, listingId)}/${encodeURIComponent(rankingId)}/entries/${encodeURIComponent(entryId)}/request`, message ? { message } : {});
    return res.data;
  }
  async listRequests(type: RankingListingType, listingId: string): Promise<RankingLinkRequest[]> {
    const res = await api.get(`${base(type, listingId)}/requests`);
    return Array.isArray(res.data) ? res.data : [];
  }
  async approveRequest(type: RankingListingType, listingId: string, requestId: string, message?: string | null): Promise<RankingLinkRequest> {
    const res = await api.post(`${base(type, listingId)}/requests/${encodeURIComponent(requestId)}/approve`, message ? { message } : {});
    return res.data;
  }
  async rejectRequest(type: RankingListingType, listingId: string, requestId: string, message?: string | null): Promise<RankingLinkRequest> {
    const res = await api.post(`${base(type, listingId)}/requests/${encodeURIComponent(requestId)}/reject`, message ? { message } : {});
    return res.data;
  }
  async myRequests(): Promise<RankingLinkRequest[]> {
    const res = await api.get('/api/rankings/my-requests');
    return Array.isArray(res.data) ? res.data : [];
  }
  async cancelMyRequest(requestId: string): Promise<RankingLinkRequest> {
    const res = await api.post(`/api/rankings/my-requests/${encodeURIComponent(requestId)}/cancel`, {});
    return res.data;
  }
  /** Öffentlich (Dialog); fail-soft → Standard */
  async getClaimSettings(type: RankingListingType, listingId: string): Promise<RankingClaimSettings> {
    try {
      const res = await api.get(`${base(type, listingId)}/claim-settings`);
      return res.data ?? DEFAULT_CLAIM_SETTINGS;
    } catch {
      return DEFAULT_CLAIM_SETTINGS;
    }
  }
  /** Bereich „Ranglisten“ */
  async setClaimSettings(type: RankingListingType, listingId: string, settings: { claimHint: string | null; claimMessageRequired: boolean }): Promise<RankingClaimSettings> {
    const res = await api.put(`${base(type, listingId)}/claim-settings`, settings);
    return res.data;
  }
  async getFeatures(type: RankingListingType, listingId: string): Promise<RankingFeatureFlags> {
    try {
      const res = await api.get(`${base(type, listingId)}/features`);
      return res.data ?? {};
    } catch {
      return {};
    }
  }
  /** Nur Plattform-Admins */
  async setFeatures(type: RankingListingType, listingId: string, flags: { rankingAi: boolean }): Promise<RankingFeatureFlags> {
    const res = await api.put(`${base(type, listingId)}/features`, flags);
    return res.data ?? {};
  }
  async importStart(type: RankingListingType, listingId: string, fileName: string, data: string): Promise<ImportJob> {
    const res = await api.post(`${base(type, listingId)}/import`, { fileName, data });
    return res.data;
  }
  async importJobs(type: RankingListingType, listingId: string): Promise<ImportJob[]> {
    const res = await api.get(`${base(type, listingId)}/import/jobs`);
    return Array.isArray(res.data) ? res.data : [];
  }
  async importJob(type: RankingListingType, listingId: string, jobId: string): Promise<ImportJob> {
    const res = await api.get(`${base(type, listingId)}/import/${encodeURIComponent(jobId)}`);
    return res.data;
  }
  async importApply(type: RankingListingType, listingId: string, jobId: string, selections: ImportSelection[]): Promise<{ job: ImportJob; created: any[]; updated: any[]; skipped: any[] }> {
    const res = await api.post(`${base(type, listingId)}/import/${encodeURIComponent(jobId)}/apply`, { selections });
    return res.data;
  }
  async importCancel(type: RankingListingType, listingId: string, jobId: string): Promise<ImportJob> {
    const res = await api.post(`${base(type, listingId)}/import/${encodeURIComponent(jobId)}/cancel`, {});
    return res.data;
  }
}

const rankingService = new RankingService();
export default rankingService;
